// Managed-account (broker "complete application") Terms & Conditions.
//
// The applicant-facing broker flow shows one acceptance checkbox per document below,
// before KYC. Each entry is an HTML document that must exist in `public/tnc/ma/`; the
// app fetches it and renders it inside a styled reading modal (ManagedAccountTncViewer).
//
// To add/remove/reorder a document: drop the .html file in `public/tnc/ma/` and add its
// filename to the list below — the display label is derived from the filename. Order
// here is the order shown.
//
// These acceptances gate the UI only; they are intentionally not persisted to the
// backend (they ride alongside the existing, persisted terms acknowledgement on the
// broker terms step). Keep that in mind before wiring them into any save payload.

const BASE_PATH = "tnc/ma";

// Just the filenames in `public/tnc/ma/` — labels are derived (filename minus .html).
const DOCUMENT_FILES = [
  "Global Managed Account Program Addendum.html",
  "Bridge Authorization Agreement and Terms.html",
  "Deposit Terms and Conditions.html",
  "Foreign Currency Deposits.html",
  "Online Banking Terms and Conditions.html",
  "Debit Card Terms and Conditions.html",
  "Privacy Policy.html",
];

const deriveLabel = (file) => String(file || "").replace(/\.html?$/i, "").trim();
const DOCUMENTS = DOCUMENT_FILES.map((file) => ({ file, label: deriveLabel(file) }));

// Build a browser URL for a document, honouring a non-root PUBLIC_URL deploy.
export const buildManagedAccountTncUrl = (file) => {
  const publicUrl = (process.env.PUBLIC_URL || "").replace(/\/+$/, "");
  const safeFile = encodeURIComponent(String(file || ""));
  return `${publicUrl}/${BASE_PATH}/${safeFile}`;
};

// Normalized, de-duplicated, render-ready document list. Defensive against a malformed
// entry so the UI degrades gracefully (no docs => no checkboxes, no gate).
export const MANAGED_ACCOUNT_TNC_DOCS = DOCUMENTS
  .filter((doc) => doc && typeof doc.file === "string" && doc.file.trim())
  .reduce((acc, doc) => {
    const file = doc.file.trim();
    if (acc.seen.has(file)) return acc;
    acc.seen.add(file);
    acc.list.push({
      file,
      label: (typeof doc.label === "string" && doc.label.trim()) || file.replace(/\.html?$/i, ""),
      url: buildManagedAccountTncUrl(file),
    });
    return acc;
  }, { seen: new Set(), list: [] })
  .list;

export const hasManagedAccountTncDocs = MANAGED_ACCOUNT_TNC_DOCS.length > 0;

// The canonical set of files that must be accepted.
export const MANAGED_ACCOUNT_TNC_FILE_KEYS = MANAGED_ACCOUNT_TNC_DOCS.map((doc) => doc.file);

// True when every published document appears in `acceptedDocs`. Vacuously true when
// there are no documents, so callers never block on an empty list.
export const areAllManagedAccountTncAccepted = (acceptedDocs, documents = MANAGED_ACCOUNT_TNC_DOCS) => {
  if (!documents.length) return true;
  const accepted = new Set(Array.isArray(acceptedDocs) ? acceptedDocs : []);
  return documents.every((doc) => accepted.has(doc.file));
};

// Toggle one document in/out of the accepted set, returning a new array that preserves
// the canonical order and contains only known files (so a stale acceptance for a removed
// PDF is dropped automatically).
export const toggleManagedAccountTncDoc = (acceptedDocs, file, checked, documents = MANAGED_ACCOUNT_TNC_DOCS) => {
  const accepted = new Set(Array.isArray(acceptedDocs) ? acceptedDocs : []);
  if (checked) {
    accepted.add(file);
  } else {
    accepted.delete(file);
  }
  return documents.map((doc) => doc.file).filter((key) => accepted.has(key));
};
